/**
 * Feriados brasileiros para os mini calendários.
 *
 * Três camadas, na ordem em que costumam aparecer num planejador:
 *
 *  1. **Nacionais fixos** — datas que não mudam de ano para ano;
 *  2. **Nacionais móveis** — calculados a partir da Páscoa (Carnaval,
 *     Sexta-feira Santa, Corpus Christi);
 *  3. **Estaduais** — uma lista curada por UF, para quem quer marcar o feriado
 *     do seu estado junto dos nacionais.
 *
 * A Páscoa sai do algoritmo de Meeus/Jones/Butcher, o mesmo que os calendários
 * litúrgicos usam — sem tabela fixa, funciona para qualquer ano.
 */
import { montarIso } from './datas.js';
export const MODOS_FERIADO = ['NENHUM', 'NACIONAIS', 'ESTADUAIS'];
export const ROTULO_MODO_FERIADO = {
    NENHUM: 'Sem feriados',
    NACIONAIS: 'Feriados nacionais',
    ESTADUAIS: 'Nacionais + estaduais',
};
/** Unidades da federação, na ordem oficial. */
export const UFS = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
];
export function ufValida(valor) {
    return UFS.includes(valor);
}
/** Feriados nacionais fixos (mês, dia, nome). */
const NACIONAIS_FIXOS = [
    [1, 1, 'Confraternização Universal'],
    [4, 21, 'Tiradentes'],
    [5, 1, 'Dia do Trabalho'],
    [9, 7, 'Independência do Brasil'],
    [10, 12, 'Nossa Senhora Aparecida'],
    [11, 2, 'Finados'],
    [11, 15, 'Proclamação da República'],
    [11, 20, 'Consciência Negra'],
    [12, 25, 'Natal'],
];
/**
 * Feriados estaduais por UF.
 *
 * Lista curada com as datas mais conhecidas de cada estado. O administrador (ou
 * o próprio usuário) pode ajustar aqui: é o único lugar que precisa mudar.
 */
const ESTADUAIS = {
    AC: [[6, 15, 'Aniversário do Acre']],
    AL: [[9, 16, 'Emancipação política de Alagoas']],
    AP: [[9, 13, 'Criação do Território Federal do Amapá']],
    AM: [[9, 5, 'Elevação do Amazonas a província']],
    BA: [[7, 2, 'Independência da Bahia']],
    CE: [[3, 25, 'Data Magna do Ceará']],
    DF: [[11, 30, 'Dia do Evangélico']],
    ES: [[5, 23, 'Colonização do solo capixaba']],
    GO: [[10, 28, 'Dia do Servidor Público']],
    MA: [[7, 28, 'Adesão do Maranhão à independência']],
    MT: [[11, 20, 'Consciência Negra']],
    MS: [[10, 11, 'Criação de Mato Grosso do Sul']],
    MG: [[4, 21, 'Data Magna de Minas Gerais']],
    PA: [[8, 15, 'Adesão do Pará à independência']],
    PB: [[8, 5, 'Emancipação política da Paraíba']],
    PR: [[12, 19, 'Emancipação política do Paraná']],
    PE: [[3, 6, 'Data Magna de Pernambuco']],
    PI: [[10, 19, 'Dia do Piauí']],
    RJ: [[4, 23, 'São Jorge']],
    RN: [[10, 3, 'Mártires de Cunhaú e Uruaçu']],
    RS: [[9, 20, 'Revolução Farroupilha']],
    RO: [[1, 4, 'Criação de Rondônia']],
    RR: [[10, 5, 'Criação de Roraima']],
    SC: [[8, 11, 'Criação da capitania de Santa Catarina']],
    SP: [[7, 9, 'Revolução Constitucionalista']],
    SE: [[7, 8, 'Emancipação política de Sergipe']],
    TO: [[10, 5, 'Criação do Tocantins']],
};
/** Domínio de Páscoa para o ano civil informado (formato YYYY-MM-DD). */
export function domingoDePascoa(ano) {
    const a = ano % 19;
    const b = Math.floor(ano / 100);
    const c = ano % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mes = Math.floor((h + l - 7 * m + 114) / 31);
    const dia = ((h + l - 7 * m + 114) % 31) + 1;
    return montarIso(ano, mes, dia);
}
function somar(iso, dias) {
    const data = new Date(`${iso}T00:00:00Z`);
    data.setUTCDate(data.getUTCDate() + dias);
    return data.toISOString().slice(0, 10);
}
function paraFeriado(iso, nome, tipo, facultativo) {
    return {
        iso,
        dia: Number.parseInt(iso.slice(-2), 10),
        mes: Number.parseInt(iso.slice(5, 7), 10),
        nome,
        tipo,
        facultativo,
    };
}
/**
 * Todos os feriados de um ano, na ordem do calendário.
 *
 * `modo` decide o que entra: `NENHUM` devolve lista vazia (o caso das agendas
 * que deixam a datação por conta de quem escreve), `NACIONAIS` traz fixos e
 * móveis, e `ESTADUAIS` acrescenta as datas da UF informada.
 */
export function feriadosDoAno(ano, modo = 'NACIONAIS', uf) {
    if (modo === 'NENHUM')
        return [];
    const lista = NACIONAIS_FIXOS.map(([mes, dia, nome]) => paraFeriado(montarIso(ano, mes, dia), nome, 'NACIONAL', false));
    // Móveis: tudo derivado da Páscoa.
    const pascoa = domingoDePascoa(ano);
    lista.push(paraFeriado(somar(pascoa, -48), 'Carnaval', 'NACIONAL', true), paraFeriado(somar(pascoa, -47), 'Carnaval', 'NACIONAL', true), paraFeriado(somar(pascoa, -2), 'Sexta-feira Santa', 'NACIONAL', false), paraFeriado(somar(pascoa, 60), 'Corpus Christi', 'NACIONAL', true));
    if (modo === 'ESTADUAIS' && uf && ufValida(uf)) {
        for (const [mes, dia, nome] of ESTADUAIS[uf]) {
            const iso = montarIso(ano, mes, dia);
            // 21/04 e 20/11 já são nacionais: evita linha repetida.
            if (lista.some((feriado) => feriado.iso === iso))
                continue;
            lista.push(paraFeriado(iso, `${nome} (${uf})`, 'ESTADUAL', false));
        }
    }
    return lista.sort((a, b) => a.iso.localeCompare(b.iso));
}
/** Feriados de um mês específico (1 a 12). */
export function feriadosDoMes(ano, mes, modo, uf) {
    return feriadosDoAno(ano, modo, uf).filter((feriado) => feriado.mes === mes);
}
/** Feriado que cai numa data, se houver. */
export function feriadoEm(iso, modo, uf) {
    if (modo === 'NENHUM')
        return null;
    const ano = Number.parseInt(iso.slice(0, 4), 10);
    return feriadosDoAno(ano, modo, uf).find((feriado) => feriado.iso === iso) ?? null;
}
/** Quantos feriados o ano tem — usado no resumo do miolo. */
export function contarFeriados(ano, modo, uf) {
    return feriadosDoAno(ano, modo, uf).length;
}
//# sourceMappingURL=feriados.js.map